import deburr from 'lodash/deburr';

// Types
interface LibraryItem {
    youtubeLink: string;
    subTopicName: string;
}

interface FilteredLibraryData {
    [key: string]: LibraryItem[];
}

interface Document {
    id: string;
    name: string;
    hasChildren: boolean;
    description?: string;
    order?: number;
    soundCloudLink?: string;
    youtubeLink?: string;
    docsLink?: string;
    summary?: string;
    verseNumbers?: string;
    isPlaylist?: boolean;
    parentName?: string;
    timeStamps?: boolean;
    tagsAndTimeStamps?: Array<{ tag: string; timeStamp: string }>;
}

interface SearchResult {
    parentName: string;
    items: Document[];
    hasChildren: boolean;
}

// Utility function to normalize text for comparison
export const normalizeText = (text: string | undefined): string => {
    if (!text) return '';

    // Custom mapping for Sanskrit diacritics
    const sanskritMap: Record<string, string> = {
        'ṛ': 'r', 'Ṛ': 'R',
        'ṇ': 'n', 'Ṇ': 'N',
        'ṅ': 'n', 'Ṅ': 'N',
        'ṭ': 't', 'Ṭ': 'T',
        'ḍ': 'd', 'Ḍ': 'D',
        'ś': 's', 'Ś': 'S',
        'ṣ': 's', 'Ṣ': 'S',
        'ṃ': 'm', 'Ṃ': 'M',
        'ḥ': 'h', 'Ḥ': 'H',
        'ā': 'a', 'Ā': 'A',
        'ī': 'i', 'Ī': 'I',
        'ū': 'u', 'Ū': 'U',
        'ñ': 'n', 'Ñ': 'N',
        'ḷ': 'l', 'Ḷ': 'L',
        'ḹ': 'l', 'Ḹ': 'L'
    };

    // Replace Sanskrit diacritics first
    let normalized = text;
    for (const [diacritic, plain] of Object.entries(sanskritMap)) {
        normalized = normalized.replace(new RegExp(diacritic, 'g'), plain);
    }

    // Then apply deburr for any remaining diacritics
    return deburr(normalized.toLowerCase().trim());
};

/**
 * Filters library data based on a search term
 * @param libraryData - The library data to filter
 * @param searchTerm - The search term to filter by
 * @returns Filtered library data
 */
export function filterLibraryData(libraryData: FilteredLibraryData, searchTerm: string): FilteredLibraryData {
    const filteredData: FilteredLibraryData = {};
    const normalizedSearchTerm = normalizeText(searchTerm);

    // Iterate over each key-value pair in the libraryData object
    for (const [key, subTopics] of Object.entries(libraryData)) {
        // Skip non-string keys
        if (typeof key !== 'string') continue;

        // Check if topic name matches
        const topicMatch = normalizeText(key).includes(normalizedSearchTerm);

        // Filter subtopics
        const filteredSubTopics = subTopics.filter(subTopic =>
            topicMatch || normalizeText(subTopic.subTopicName).includes(normalizedSearchTerm)
        );

        // Only add non-empty results
        if (filteredSubTopics.length > 0) {
            filteredData[key] = filteredSubTopics;
        }
    }

    return filteredData;
}

/**
 * Checks if all values in an object are empty arrays
 * @param obj - The object to check
 * @returns True if all values are empty arrays
 */
export function areAllValuesEmptyArrays(obj: Record<string, unknown[]>): boolean {
    return Object.values(obj).every(value => Array.isArray(value) && value.length === 0);
}

/**
 * Filters and groups documents based on a search value
 * @param value - The search value
 * @param documents - The documents to filter
 * @returns Filtered and grouped documents
 */
export function filterDocs(value: string, documents: Document[]): SearchResult[] {
    // Early return for invalid input
    if (!value || !Array.isArray(documents)) {
        return [];
    }

    const normalizedSearchTerm = normalizeText(value);

    // Filter documents
    const filteredList = documents.filter(item => {
        // Skip items with children
        if (item.hasChildren) return false;

        // Check for matches in name, description and summary
        const nameMatch = normalizeText(item.name).includes(normalizedSearchTerm);
        const descriptionMatch = item.description ? normalizeText(item.description).includes(normalizedSearchTerm) : false;
        const summaryMatch = item.summary ? normalizeText(item.summary).includes(normalizedSearchTerm) : false;

        // Check for matches in verse numbers (exact match in comma-separated string)
        const verseMatch = item.verseNumbers ?
            item.verseNumbers.split(',').some(verse =>
                normalizeText(verse.trim()) === normalizedSearchTerm
            ) : false;

        return nameMatch || descriptionMatch || summaryMatch || verseMatch;
    });

    // Group filtered documents by parent name
    const grouped = filteredList.reduce<Record<string, Document[]>>((accumulator, current) => {
        const key = current.parentName || 'Uncategorized';
        if (!accumulator[key]) {
            accumulator[key] = [];
        }
        accumulator[key].push(current);
        return accumulator;
    }, {});

    // Convert grouped results to array format
    return Object.entries(grouped).map(([key, items]) => ({
        parentName: key,
        items,
        hasChildren: false
    }));
}
